import { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useKnowledge } from "../hooks/useKnowledge";
import { useWorkspace } from "@/shared/hooks/api/useWorkspace";
import { useToast } from "@/shared/hooks/use-toast";
import { useKnowledgeStorage } from "../hooks/useKnowledgeStorage";
import { KnowledgeStorage as KnowledgeStorageType } from "../types/knowledge-storage";
import { deleteKnowledgeFile, downloadKnowledgeFile } from "../services/storageService";
import { useStorageSSE } from "../hooks/useStorageSSE";
import { Button } from "@/shared/components/ui/button";
import { CategoryFilter, FilterState, ColumnState } from "@/shared/components/ui/category-filter";
import { ImportToolSelector } from "./ImportToolSelector";
import { ImportToolForm } from "./ImportToolForm";
import { ImportTool } from "../types/import-tool";
import { Card } from "@/shared/components/ui/card";
import { Trash, Search, Filter, Plus, X, Download, Loader2, Info, MoreHorizontal, FileText, Tags, ChevronDown, Layers, Star, StarHalf, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import { Checkbox } from "@/shared/components/ui/checkbox";
import { DatePicker } from "@/shared/components/ui/date-picker";
import { format } from "date-fns";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from "@/shared/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { Input } from "@/shared/components/ui/input";
import { Skeleton } from "@/shared/components/ui/skeleton";
import { Alert, AlertDescription } from "@/shared/components/ui/alert";
import { Badge } from "@/shared/components/ui/badge";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious
} from "@/shared/components/ui/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { useQueryClient } from "@tanstack/react-query";

import { EntryDetails } from "./EntryDetails";


export function KnowledgeStorageComponent() {
  const { t } = useTranslation();
  const { currentWorkspace } = useWorkspace();
  const { toast } = useToast();
  const { data: knowledge, isLoading: isLoadingKnowledge, error: knowledgeError } = useKnowledge();
  const [isKnowledgeImportDialogOpen, setIsKnowledgeImportDialogOpen] = useState(false);
  const [selectedTool, setSelectedTool] = useState<ImportTool | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchDate, setSearchDate] = useState<Date | undefined>();
  const [searchFieldType, setSearchFieldType] = useState<'text' | 'date'>('text');
  const [filters, setFilters] = useState<FilterState>({});
  const [visibleColumns, setVisibleColumns] = useState<ColumnState>({
    name: true,
    updated_at: false,
    file_size: false,
    status: false,
    quality_score: false,
    actions: false
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  // Ordenação server-side. Padrão: "Atualizado Em" decrescente (mais novo
  // primeiro). Clicar num cabeçalho ordena por ele; clicar de novo inverte.
  const [sortBy, setSortBy] = useState<string>('updated_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [selectedEntry, setSelectedEntry] = useState<KnowledgeStorageType | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deletingFileId, setDeletingFileId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  // Filtros client-side aplicados sobre a página atual. Sets aceitam multi-select:
  //   - selectedTypes:  storage.type ∈ { ... }
  //   - selectedFacets: any facet do doc casa com a chave 'name:value'
  // Os quick filters somam (AND) aos filtros server-side (search + category
  // filter). Como podem ficar invisíveis quando o usuário muda a busca,
  // resetamos a seleção sempre que search ou filtros server-side mudam —
  // evita o caso "busquei X e não veio nada porque tinha um quick filter
  // ativo escondido". Visualmente: quando há search/filter, o card 'Todos'
  // exibe o count da página filtrada (não absoluto), reforçando que os quick
  // filters operam sobre o resultado da busca.
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [selectedFacets, setSelectedFacets] = useState<string[]>([]);
  // Largura das colunas em pixels. Mínimos amplos pra que nada estoure o
  // layout no carregamento inicial. Usuário pode redimensionar via drag no
  // header — cada coluna respeita o min interno via `Math.max` no handler.
  // Estado in-memory por simplicidade; recarregar a tela reseta defaults.
  const [colWidths, setColWidths] = useState<Record<string, number>>({
    name: 240,
    updated_at: 180,
    file_size: 100,
    status: 120,
    quality: 140,
    actions: 80,
  });

  /**
   * Inicia o drag de resize de uma coluna. Calcula delta em relação ao X
   * inicial do mousedown e propaga via setState. Cleanup no mouseup.
   */
  const startColResize = (colId: string, minWidth = 60) => (e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = colWidths[colId] ?? 120;
    const onMove = (ev: MouseEvent) => {
      const delta = ev.clientX - startX;
      setColWidths((prev) => ({ ...prev, [colId]: Math.max(minWidth, startW + delta) }));
    };
    const onUp = () => {
      document.body.style.cursor = '';
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    document.body.style.cursor = 'col-resize';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const effectiveSearchTerm = searchFieldType === 'text'
    ? searchTerm
    : (searchDate ? format(searchDate, 'yyyy-MM-dd') : '');

  const filterParams = useMemo(() => {
    return Object.entries(filters).reduce((acc, [key, values]) => {
      if (values && values.length > 0) {
        acc[key] = values;
      }
      return acc;
    }, {} as Record<string, string[]>);
  }, [filters]);

  const {
    data: storageData,
    isLoading: isLoadingStorage,
    error,
    refetch,
  } = useKnowledgeStorage(
    knowledge?.id || "",
    {
      page: currentPage,
      pageSize,
      searchTerm: effectiveSearchTerm || undefined,
      filters: filterParams,
      searchFieldType,
      sortBy,
      sortOrder
    },
    visibleColumns
  );

  // Ordena por uma coluna. Mesma coluna → inverte direção; coluna nova →
  // começa ascendente. Sempre volta para a página 1 (a ordenação é global).
  const handleSort = (column: string) => {
    setCurrentPage(1);
    if (sortBy === column) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  // ── Derivações para os cards seletores (Tipo / Facets) ──────────────────
  // Tipos únicos presentes nos docs da página atual. Ordenados pra UX previsível.
  const availableTypes = useMemo<string[]>(() => {
    const set = new Set<string>();
    for (const d of storageData?.data || []) {
      if (d.type && typeof d.type === 'string') set.add(d.type);
    }
    return Array.from(set).sort();
  }, [storageData?.data]);

  // Facets únicas (chave `name:value`) presentes em metadata.facets dos docs.
  // Cada doc pode ter N facets; aqui aglutinamos pra montar o seletor.
  const availableFacets = useMemo<Array<{ key: string; name: string; value: string }>>(() => {
    const seen = new Map<string, { name: string; value: string }>();
    for (const d of storageData?.data || []) {
      const facets = (d.metadata as { facets?: unknown } | undefined)?.facets;
      if (!Array.isArray(facets)) continue;
      for (const f of facets) {
        if (!f || typeof f !== 'object') continue;
        const name = (f as { name?: unknown }).name;
        const value = (f as { value?: unknown }).value;
        if (typeof name !== 'string' || typeof value !== 'string') continue;
        const key = `${name}:${value}`;
        if (!seen.has(key)) seen.set(key, { name, value });
      }
    }
    return Array.from(seen.entries())
      .map(([key, v]) => ({ key, name: v.name, value: v.value }))
      .sort((a, b) => a.key.localeCompare(b.key));
  }, [storageData?.data]);

  // Aplica os filtros locais sobre a página atual. Todos com AND entre si.
  const filteredData = useMemo<KnowledgeStorageType[]>(() => {
    const rows = storageData?.data || [];
    if (selectedTypes.length === 0 && selectedFacets.length === 0) return rows;
    return rows.filter((entry) => {
      if (selectedTypes.length > 0) {
        if (!entry.type || !selectedTypes.includes(entry.type)) return false;
      }
      if (selectedFacets.length > 0) {
        const docFacets = (entry.metadata as { facets?: unknown } | undefined)?.facets;
        const keys = new Set<string>();
        if (Array.isArray(docFacets)) {
          for (const f of docFacets) {
            if (f && typeof f === 'object'
              && typeof (f as { name?: unknown }).name === 'string'
              && typeof (f as { value?: unknown }).value === 'string') {
              keys.add(`${(f as { name: string }).name}:${(f as { value: string }).value}`);
            }
          }
        }
        const matchesAny = selectedFacets.some((sf) => keys.has(sf));
        if (!matchesAny) return false;
      }
      return true;
    });
  }, [storageData?.data, selectedTypes, selectedFacets]);

  const clearQuickFilters = () => {
    setSelectedTypes([]);
    setSelectedFacets([]);
  };

  // Hook SSE para atualizações em tempo real
  const {
    items: sseItems,
    total: _sseTotal,
    isConnected: _isConnected,
    error: _sseError,
    embeddingProgress
  } = useStorageSSE({
    knowledgeBaseId: knowledge?.id || "",
    enabled: !!knowledge?.id
  });

  // Defense in depth: trackeia o maior progresso já visto por entry para que
  // a pill nunca regrida visualmente, mesmo em cenários de race condition
  // (SSE entregando 0% após 100%, ou progress limpo prematuramente).
  const maxSeenProgress = useRef<Map<string, number>>(new Map());

  const queryClient = useQueryClient();

  // Invalidar queries quando há mudanças via SSE
  useEffect(() => {
    if (sseItems.length > 0) {
      queryClient.invalidateQueries({ queryKey: ['knowledge-storage'] });
    }
  }, [sseItems, queryClient]);

  // Reseta os quick filters sempre que a busca ou os filtros server-side
  // mudam. Sem isso, um quick filter selecionado anteriormente pode esconder
  // resultados da nova busca sem o usuário perceber.
  useEffect(() => {
    setSelectedTypes([]);
    setSelectedFacets([]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, searchDate, filterParams]);

  const setFieldType = (type: 'text' | 'date') => {
    setSearchFieldType(type);

    if (type === 'text') {
      setSearchDate(undefined);
    } else {
      setSearchTerm("");
    }
  };

  useEffect(() => {
    if (knowledge?.id) {
      const timeoutId = setTimeout(() => {
        refetch();
      }, 50);

      return () => clearTimeout(timeoutId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, searchDate, filterParams, visibleColumns, knowledge?.id, currentPage, pageSize, sortBy, sortOrder]);

  if (!knowledge?.id) {
    console.warn("[KnowledgeStorage] No active knowledge found for workspace:", currentWorkspace?.id);
  }

  // Renderiza o conteúdo da tabela
  const renderTableContent = () => {
    if (isLoadingKnowledge || isLoadingStorage) {
      return Array(3).fill(0).map((_, index) => (
        <TableRow key={index}>
          <TableCell><Skeleton className="h-4 w-[180px]" /></TableCell>
          <TableCell><Skeleton className="h-4 w-[120px]" /></TableCell>
          <TableCell><Skeleton className="h-4 w-[60px]" /></TableCell>
          <TableCell><Skeleton className="h-4 w-[80px]" /></TableCell>
          <TableCell><Skeleton className="h-4 w-[50px]" /></TableCell>
          <TableCell><Skeleton className="h-4 w-[100px]" /></TableCell>
        </TableRow>
      ));
    }

    if (!currentWorkspace?.id) {
      return (
        <TableRow>
          <TableCell colSpan={6} className="text-center">
            <Alert>
              <AlertDescription>
                {t('knowledge.imports.no_workspace')}
              </AlertDescription>
            </Alert>
          </TableCell>
        </TableRow>
      );
    }

    if (!knowledge?.id) {
      return (
        <TableRow>
          <TableCell colSpan={6} className="text-center">
            <Alert>
              <AlertDescription>
                {t('knowledge.imports.no_knowledge')}
              </AlertDescription>
            </Alert>
          </TableCell>
        </TableRow>
      );
    }

    if (knowledgeError) {
      console.error("Knowledge Error:", knowledgeError);
      return (
        <TableRow>
          <TableCell colSpan={6} className="text-center">
            <Alert variant="destructive">
              <AlertDescription>
                {t('knowledge.imports.error_loading')}
              </AlertDescription>
            </Alert>
          </TableCell>
        </TableRow>
      );
    }

    if (error) {
      console.error("Storage Error:", error);
      return (
        <TableRow>
          <TableCell colSpan={6} className="text-center">
            <Alert variant="destructive">
              <AlertDescription>
                {error instanceof Error
                  ? error.message
                  : t('knowledge.imports.error_loading')}
              </AlertDescription>
            </Alert>
          </TableCell>
        </TableRow>
      );
    }

    if (!storageData?.data?.length) {
      return (
        <TableRow>
          <TableCell colSpan={6} className="text-center">
            {t('knowledge.imports.no_data')}
          </TableCell>
        </TableRow>
      );
    }

    if (filteredData.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={5} className="text-center text-sm text-muted-foreground py-6">
            {t('knowledge.storage.quickFilters.noMatches', 'Nenhum documento corresponde aos filtros aplicados')}
          </TableCell>
        </TableRow>
      );
    }

    return filteredData.map((entry) => (
      <TableRow key={entry.id}>
        <TableCell className="overflow-hidden">
          <div className="flex items-center gap-2.5 min-w-0">
            <img
              src={entry.tool_logo_url}
              alt={entry.type || ''}
              className="w-7 h-7 object-contain shrink-0"
              onError={(e) => {
                e.currentTarget.src = "/placeholder-icon.png";
              }}
            />
            <span className="truncate min-w-0" title={entry.name}>{entry.name}</span>
          </div>
        </TableCell>
        <TableCell>{new Date(entry.updated_at || entry.created_at).toLocaleString()}</TableCell>
        <TableCell className="text-sm text-muted-foreground tabular-nums">
          {formatBytes(entry.file_size)}
        </TableCell>
        <TableCell>
          {(() => {
            const isProcessing = entry.status === 'processing';
            const isSuccess = entry.status === 'active' || entry.status === 'completed' || entry.status === 'embedded';

            // Pill unificada para processing/embedded: mesma estrutura DOM,
            // só muda cor de fundo + texto. Evita unmount/remount entre os
            // dois estados — a transição de "X%" → "Assimilado" fica suave,
            // sem flash de 0% causado pela troca de componentes.
            if (isProcessing || isSuccess) {
              const progress = embeddingProgress.get(entry.id);
              // Combina três fontes pra calcular o valor exibido, sempre
              // monotônico crescente: (1) maior valor já visto no ref,
              // (2) valor corrente do SSE, (3) 1% como piso para o estado
              // "começou mas não recebeu evento ainda". Quando isSuccess,
              // força 100% pra evitar qualquer regressão visual.
              const seen = Math.max(
                maxSeenProgress.current.get(entry.id) ?? 0,
                progress?.percentComplete ?? 0,
              );
              if (seen > (maxSeenProgress.current.get(entry.id) ?? 0)) {
                maxSeenProgress.current.set(entry.id, seen);
              }
              const value = isSuccess ? 100 : Math.max(seen, 1);
              const progressText = (progress || seen > 0)
                ? `${value}%`
                : t(`knowledge.storage.status.${entry.status}`);
              return (
                <div
                  className={`relative inline-flex items-center justify-center overflow-hidden rounded-full border border-transparent px-2.5 py-0.5 text-xs font-semibold transition-colors duration-300 ${
                    isSuccess ? 'bg-green-500 text-white' : 'bg-secondary text-secondary-foreground'
                  }`}
                >
                  {/* Fill bar — fade out ao concluir, mas mantém o DOM */}
                  <div
                    className={`absolute inset-y-0 left-0 transition-all duration-300 ease-out ${
                      isSuccess ? 'opacity-0' : 'bg-primary/25'
                    }`}
                    style={{ width: `${value}%` }}
                  />
                  {/* Sizer invisível: largura natural do texto "Assimilado" */}
                  <span aria-hidden className="invisible select-none">
                    {t('knowledge.storage.status.embedded')}
                  </span>
                  {/* Texto de progresso (X%): fade out quando completa */}
                  <span
                    className={`absolute inset-0 z-10 flex items-center justify-center tabular-nums transition-opacity duration-300 ${
                      isSuccess ? 'opacity-0' : 'opacity-100'
                    }`}
                    aria-hidden={isSuccess}
                  >
                    {progressText}
                  </span>
                  {/* Texto "Assimilado": fade in quando completa */}
                  <span
                    className={`absolute inset-0 z-10 flex items-center justify-center transition-opacity duration-300 ${
                      isSuccess ? 'opacity-100 delay-150' : 'opacity-0'
                    }`}
                    aria-hidden={!isSuccess}
                  >
                    {t('knowledge.storage.status.embedded')}
                  </span>
                </div>
              );
            }

            return (
              <Badge variant={entry.status === 'failed' ? 'destructive' : 'warning'}>
                {t(`knowledge.storage.status.${entry.status}`) || entry.status}
              </Badge>
            );
          })()}
        </TableCell>
        <TableCell>
          {entry.status === 'embedded' && entry.config?.quality_score != null ? (
            <QualityStars score={Number(entry.config.quality_score)} />
          ) : null}
        </TableCell>
        <TableCell className="sticky right-0 bg-background shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.08)]">
          <div className="flex items-center justify-center">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" title={t('common.actionButtons')}>
                  <MoreHorizontal className="h-4 w-4" />
                  <span className="sr-only">{t('common.actionButtons')}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => setSelectedEntry(entry)}>
                  <Info className="mr-2 h-4 w-4" />
                  {t('knowledge.storage.actions.info')}
                </DropdownMenuItem>
                {entry.file_path && (
                  <DropdownMenuItem onSelect={() => handleDownloadClick(entry.id)}>
                    <Download className="mr-2 h-4 w-4" />
                    {t('common.actions.download')}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onSelect={() => handleDeleteClick(entry.id)}
                  className="text-red-600 focus:text-red-700"
                >
                  <Trash className="mr-2 h-4 w-4" />
                  {t('common.actions.delete')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </TableCell>
      </TableRow>
    ));
  };

  const handleDownloadClick = async (id: string) => {
    try {
      toast({
        title: t('knowledge.storage.messages.downloading'),
      });
      
      await downloadKnowledgeFile(id);
      
      toast({
        title: t('knowledge.storage.messages.downloadSuccess'),
      });
    } catch (error) {
      toast({
        title: t('knowledge.storage.errors.downloading'),
        description: t('knowledge.storage.errors.downloadFailed'),
        variant: 'destructive',
      });
    }
  };

  const handleDeleteClick = (id: string) => {
    setDeletingFileId(id);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingFileId) return;

    try {
      setIsDeleting(true);
      await deleteKnowledgeFile(deletingFileId);

      // Invalidar todas as queries de knowledge-storage para este KB (qualquer page/filter)
      await queryClient.invalidateQueries({
        predicate: (query) =>
          Array.isArray(query.queryKey) &&
          query.queryKey[0] === 'knowledge-storage' &&
          query.queryKey[1] === knowledge?.id
      });

      // Forçar refetch da página atual
      await refetch();

      toast({
        title: t('common.messages.deleteSuccess'),
        description: t('common.messages.deleteSuccessDescription'),
      });
    } catch (error) {
      console.error('Error deleting file:', error);
      toast({
        variant: "destructive",
        title: t('common.error'),
        description: t('common.messages.deleteError'),
      });
    } finally {
      setIsDeleting(false);
      setIsDeleteDialogOpen(false);
      setDeletingFileId(null);
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <div className="p-4 space-y-4">
          <div className="flex justify-between items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              {searchFieldType === 'text' ? (
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground z-10 pointer-events-none" />
                  <Input
                    placeholder={t('knowledge.storage.actions.search')}
                    className="pl-8 w-[250px]"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  {searchTerm && (
                    <Button
                      variant="ghost"
                      className="absolute right-0 top-0 h-9 w-9 p-0"
                      onClick={() => setSearchTerm("")}
                    >
                      <X className="h-4 w-4" />
                      <span className="sr-only">{t('knowledge.imports.actions.clear')}</span>
                    </Button>
                  )}
                </div>
              ) : (
                <div className="relative w-[250px]">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground z-10" />
                  <DatePicker
                    value={searchDate}
                    onChange={(date: Date | undefined) => setSearchDate(date)}
                    placeholder={t('knowledge.storage.actions.search')}
                    width="250px"
                    className="pl-8 w-[250px]"
                  />
                </div>
              )}
              <CategoryFilter
                trigger={
                  <Button variant="outline" size="icon">
                    <Filter className="h-4 w-4" />
                    <span className="sr-only">{t('knowledge.actions.filter')}</span>
                  </Button>
                }
                selectedFilters={filters}
                onChange={setFilters}
                onClear={() => {
                  setFilters({});

                  setFieldType('text');

                  setVisibleColumns({
                    name: true,
                    updated_at: false,
                    file_size: false,
                    status: false,
                    quality_score: false,
                    actions: false
                  });
                }}
                columns={[
                  { id: 'name', name: t('knowledge.imports.fields.name'), type: 'text' },
                  { id: 'updated_at', name: t('knowledge.imports.fields.updated_at'), type: 'date' },
                  { id: 'file_size', name: t('knowledge.imports.fields.file_size'), type: 'text' },
                  { id: 'status', name: t('knowledge.imports.fields.status'), type: 'text' },
                  { id: 'quality_score', name: t('knowledge.imports.fields.quality'), type: 'text' },
                ]}
                selectedColumns={visibleColumns}
                onColumnChange={(newColumns) => {

                  const wasDateColumnActive = visibleColumns['updated_at'];
                  const isDateColumnActive = newColumns['updated_at'];

                  if (isDateColumnActive && !wasDateColumnActive) {
                    setFieldType('date');
                  } else if (!isDateColumnActive && wasDateColumnActive) {
                    setFieldType('text');
                  }

                  setVisibleColumns(newColumns);
                }}
              />

              {/* PBI 79328 — quick filters na mesma linha do search, à direita
                  do filtro de colunas. Operam client-side sobre a página atual
                  e zeram automaticamente quando o usuário muda search/filter
                  pra evitar resultados ocultos. */}
              {(storageData?.data?.length ?? 0) > 0 && (
                <>
                  <QuickFilterCard
                    active={selectedTypes.length === 0 && selectedFacets.length === 0}
                    onClick={clearQuickFilters}
                    label={t('knowledge.storage.quickFilters.all', 'Todos')}
                    count={storageData?.data?.length ?? 0}
                    icon={<Layers className="h-3.5 w-3.5" />}
                  />

                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className={`flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm transition-colors hover:bg-accent ${
                          selectedTypes.length > 0 ? 'border-primary/40 bg-primary/5 text-foreground' : 'border-border'
                        }`}
                      >
                        <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                        <span>{t('knowledge.storage.quickFilters.type', 'Tipo')}</span>
                        {selectedTypes.length > 0 && (
                          <Badge variant="secondary" className="h-5 px-1.5 text-[10px] font-medium">
                            {selectedTypes.length}
                          </Badge>
                        )}
                        <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="w-56 p-2">
                      {availableTypes.length === 0 ? (
                        <p className="text-xs text-muted-foreground p-2">
                          {t('knowledge.storage.quickFilters.emptyTypes', 'Sem tipos disponíveis')}
                        </p>
                      ) : (
                        <div className="space-y-1">
                          {availableTypes.map((t2) => {
                            const checked = selectedTypes.includes(t2);
                            return (
                              <label
                                key={t2}
                                className="flex items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-accent cursor-pointer"
                              >
                                <Checkbox
                                  checked={checked}
                                  onCheckedChange={(v) => {
                                    setSelectedTypes((prev) =>
                                      v ? [...prev, t2] : prev.filter((x) => x !== t2),
                                    );
                                  }}
                                />
                                <span className="capitalize">{t2}</span>
                              </label>
                            );
                          })}
                          {selectedTypes.length > 0 && (
                            <button
                              type="button"
                              onClick={() => setSelectedTypes([])}
                              className="w-full text-left text-xs text-muted-foreground hover:text-foreground px-2 py-1.5 mt-1 border-t pt-2"
                            >
                              {t('knowledge.storage.quickFilters.clear', 'Limpar')}
                            </button>
                          )}
                        </div>
                      )}
                    </PopoverContent>
                  </Popover>

                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className={`flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm transition-colors hover:bg-accent ${
                          selectedFacets.length > 0 ? 'border-primary/40 bg-primary/5 text-foreground' : 'border-border'
                        }`}
                      >
                        <Tags className="h-3.5 w-3.5 text-muted-foreground" />
                        <span>{t('knowledge.storage.quickFilters.facets', 'Facets')}</span>
                        {selectedFacets.length > 0 && (
                          <Badge variant="secondary" className="h-5 px-1.5 text-[10px] font-medium">
                            {selectedFacets.length}
                          </Badge>
                        )}
                        <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="w-64 p-2">
                      {availableFacets.length === 0 ? (
                        <p className="text-xs text-muted-foreground p-2">
                          {t('knowledge.storage.quickFilters.emptyFacets', 'Sem facets disponíveis')}
                        </p>
                      ) : (
                        <div className="space-y-1 max-h-72 overflow-y-auto">
                          {availableFacets.map((f) => {
                            const checked = selectedFacets.includes(f.key);
                            return (
                              <label
                                key={f.key}
                                className="flex items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-accent cursor-pointer"
                              >
                                <Checkbox
                                  checked={checked}
                                  onCheckedChange={(v) => {
                                    setSelectedFacets((prev) =>
                                      v ? [...prev, f.key] : prev.filter((x) => x !== f.key),
                                    );
                                  }}
                                />
                                <span className="text-xs">
                                  <span className="text-muted-foreground">{f.name}</span>
                                  <span className="text-muted-foreground/60 mx-1">·</span>
                                  <span>{f.value}</span>
                                </span>
                              </label>
                            );
                          })}
                          {selectedFacets.length > 0 && (
                            <button
                              type="button"
                              onClick={() => setSelectedFacets([])}
                              className="w-full text-left text-xs text-muted-foreground hover:text-foreground px-2 py-1.5 mt-1 border-t pt-2"
                            >
                              {t('knowledge.storage.quickFilters.clear', 'Limpar')}
                            </button>
                          )}
                        </div>
                      )}
                    </PopoverContent>
                  </Popover>
                </>
              )}
            </div>
            <div className="flex items-center space-x-2">
              <Button onClick={() => setIsKnowledgeImportDialogOpen(true)} data-tour="upload-button">
                <Plus className="mr-2 h-4 w-4" />
                {t('knowledge.actions.upload')}
              </Button>
            </div>
          </div>

          <div className="rounded-md border overflow-auto">
            {/* table-layout fixed faz o browser respeitar as larguras do
                <colgroup>. A largura total da <table> é a soma exata das
                colunas (não max-content) — sem isso o browser usa min-content
                e o resize pra menos não tem efeito visual nas cells.
                Cada <TableCell> recebe overflow-hidden pra que `truncate`
                funcione corretamente. */}
            <Table
              style={{
                tableLayout: 'fixed',
                // width=100% faz a tabela preencher o container; minWidth
                // segura a soma das colunas pra quando o container é pequeno
                // (scroll horizontal aparece naturalmente). Combinado com
                // <col> widths, o navegador respeita os pixels exatos das
                // colunas e distribui o restante na primeira coluna sem
                // largura explícita (na prática, name vira flex residual).
                width: '100%',
                minWidth:
                  colWidths.name
                  + colWidths.updated_at
                  + colWidths.file_size
                  + colWidths.status
                  + colWidths.quality
                  + colWidths.actions,
              }}
            >
              <colgroup>
                {/* name fica sem width explícita → vira flexível e absorve
                    o espaço restante quando a soma das outras colunas é
                    menor que o container. minWidth do <td> garante limite
                    inferior. */}
                <col style={{ minWidth: colWidths.name }} />
                <col style={{ width: colWidths.updated_at }} />
                <col style={{ width: colWidths.file_size }} />
                <col style={{ width: colWidths.status }} />
                <col style={{ width: colWidths.quality }} />
                <col style={{ width: colWidths.actions }} />
              </colgroup>
              <TableHeader>
                <TableRow>
                  <ResizableTableHead onResize={startColResize('name')} sortKey="name" activeSortKey={sortBy} sortOrder={sortOrder} onSort={handleSort}>
                    {t('knowledge.imports.fields.name')}
                  </ResizableTableHead>
                  <ResizableTableHead onResize={startColResize('updated_at')} sortKey="updated_at" activeSortKey={sortBy} sortOrder={sortOrder} onSort={handleSort}>
                    {t('knowledge.imports.fields.updated_at')}
                  </ResizableTableHead>
                  <ResizableTableHead onResize={startColResize('file_size')} sortKey="file_size" activeSortKey={sortBy} sortOrder={sortOrder} onSort={handleSort}>
                    {t('knowledge.imports.fields.file_size')}
                  </ResizableTableHead>
                  <ResizableTableHead onResize={startColResize('status')} sortKey="status" activeSortKey={sortBy} sortOrder={sortOrder} onSort={handleSort}>
                    {t('knowledge.imports.fields.status')}
                  </ResizableTableHead>
                  <ResizableTableHead onResize={startColResize('quality')} sortKey="quality_score" activeSortKey={sortBy} sortOrder={sortOrder} onSort={handleSort}>
                    {t('knowledge.imports.fields.quality')}
                  </ResizableTableHead>
                  {/* Coluna Ações fixa à direita: continua visível durante o
                      scroll horizontal. bg-background opaco evita que o
                      conteúdo da coluna anterior vaze por baixo; shadow
                      esquerda dá um indicador visual de "essa coluna flutua
                      sobre o conteúdo". */}
                  <TableHead className="text-center sticky right-0 bg-background shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.08)]">
                    {t('common.actionButtons')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {renderTableContent()}
              </TableBody>
            </Table>
          </div>

          {!!storageData && (storageData?.total ?? 0) > 0 && (
            <div className="flex items-center justify-between px-4 mt-4">
              <div className="flex-1 text-sm text-muted-foreground">
                {isLoadingStorage ? (
                  <Skeleton className="h-4 w-[250px]" />
                ) : (
                  t('knowledge.storage.showing', {
                    start: (currentPage - 1) * pageSize + 1,
                    end: Math.min(currentPage * pageSize, storageData?.total || 0),
                    total: storageData?.total || 0
                  })
                )}
              </div>

              <div className="flex-1 flex justify-center items-center">
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      {currentPage > 1 ? (
                        <PaginationPrevious
                          onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        />
                      ) : (
                        <span className="opacity-50">
                          <PaginationPrevious />
                        </span>
                      )}
                    </PaginationItem>

                    {/* Primeira página */}
                    {currentPage > 2 && (
                      <PaginationItem>
                        <PaginationLink onClick={() => setCurrentPage(1)}>
                          1
                        </PaginationLink>
                      </PaginationItem>
                    )}

                    {/* Ellipsis no início */}
                    {currentPage > 3 && (
                      <PaginationItem>
                        <PaginationEllipsis />
                      </PaginationItem>
                    )}

                    {/* Página anterior (se não for primeira) */}
                    {currentPage > 1 && (
                      <PaginationItem>
                        <PaginationLink onClick={() => setCurrentPage(currentPage - 1)}>
                          {currentPage - 1}
                        </PaginationLink>
                      </PaginationItem>
                    )}

                    {/* Página atual */}
                    <PaginationItem>
                      <PaginationLink isActive>
                        {currentPage}
                      </PaginationLink>
                    </PaginationItem>

                    {/* Próxima página */}
                    {currentPage < Math.ceil((storageData?.total ?? 0) / pageSize) && (
                      <PaginationItem>
                        <PaginationLink onClick={() => setCurrentPage(currentPage + 1)}>
                          {currentPage + 1}
                        </PaginationLink>
                      </PaginationItem>
                    )}

                    {/* Ellipsis no final */}
                    {currentPage < Math.ceil((storageData?.total ?? 0) / pageSize) - 2 && (
                      <PaginationItem>
                        <PaginationEllipsis />
                      </PaginationItem>
                    )}

                    {/* Última página */}
                    {currentPage < Math.ceil((storageData?.total ?? 0) / pageSize) - 1 && (
                      <PaginationItem>
                        <PaginationLink onClick={() => setCurrentPage(Math.ceil((storageData?.total ?? 0) / pageSize))}>
                          {Math.ceil((storageData?.total ?? 0) / pageSize)}
                        </PaginationLink>
                      </PaginationItem>
                    )}

                    <PaginationItem>
                      {currentPage < Math.ceil((storageData?.total ?? 0) / pageSize) ? (
                        <PaginationNext
                          onClick={() => setCurrentPage(Math.min(Math.ceil((storageData?.total ?? 0) / pageSize), currentPage + 1))}
                        />
                      ) : (
                        <span className="opacity-50">
                          <PaginationNext />
                        </span>
                      )}
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </div>

              <div className="flex items-center gap-2 justify-end">
                <span className="text-sm text-muted-foreground">{t('knowledge.storage.itemsPerPage')}</span>
                <Select
                  value={pageSize.toString()}
                  onValueChange={(value) => {
                    setPageSize(parseInt(value));
                    setCurrentPage(1);
                  }}
                >
                  <SelectTrigger className="w-[70px]">
                    <SelectValue placeholder={pageSize.toString()} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5</SelectItem>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Seletor de ferramenta de importação (usa Sheet internamente) */}
      <ImportToolSelector
        open={isKnowledgeImportDialogOpen && !selectedTool}
        onClose={() => setIsKnowledgeImportDialogOpen(false)}
        onSelect={(tool) => {
          setSelectedTool(tool);
        }}
        categoryFilter="Files"
      />

      {/* Diálogo para o formulário de configuração da ferramenta */}
      {selectedTool && (
        <ImportToolForm
          tool={selectedTool}
          onSubmit={(data) => {
            // Verificar se há uma resposta de upload
            if (data.uploadResponse) {
              // Invalidar a query para forçar uma atualização da lista
              queryClient.invalidateQueries({
                queryKey: ["knowledge-storage", knowledge?.id],
              });

              // Disparar evento para tour completar step de upload
              window.dispatchEvent(new Event('tour-document-uploaded'));
            }
          }}
          onCancel={() => {
            setSelectedTool(null);
            setIsKnowledgeImportDialogOpen(false);
          }}
          open={!!selectedTool && isKnowledgeImportDialogOpen}
          onOpenChange={(open) => {
            if (!open) {
              setSelectedTool(null);
              setIsKnowledgeImportDialogOpen(false);
            }
          }}
        />
      )}

      {selectedEntry && (() => {
        const entrySource = selectedEntry.category === 'Files'
          ? t('knowledge.storage.source.manual')
          : t('knowledge.storage.source.automatic');

        return (
        <Dialog open={!!selectedEntry} onOpenChange={() => setSelectedEntry(null)}>
          <DialogContent className="max-w-3xl h-[680px] flex flex-col p-0 gap-0">
            <DialogHeader className="px-6 py-4 border-b space-y-0">
              <div className="flex items-center gap-3">
                <div className="shrink-0 h-9 w-9 rounded-md bg-muted/50 border flex items-center justify-center overflow-hidden">
                  {selectedEntry.tool_logo_url ? (
                    <img
                      src={selectedEntry.tool_logo_url}
                      alt={selectedEntry.type || ''}
                      className="h-7 w-7 object-contain"
                      onError={(e) => {
                        // Esconde a imagem quebrada — o container revela o
                        // ícone fallback que mora atrás.
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  ) : (
                    <FileText className="h-4 w-4 text-muted-foreground" />
                  )}
                </div>
                <div className="min-w-0 flex-1 flex items-center gap-2">
                  <DialogTitle className="text-base font-semibold tracking-tight truncate">
                    {selectedEntry.name || selectedEntry.file_name || t('knowledge.storage.details.title')}
                  </DialogTitle>
                  <Badge variant="outline" className="shrink-0 capitalize text-[11px] font-normal">
                    {entrySource}
                  </Badge>
                </div>
              </div>
              {/* Descrição acessível para screen readers — a versão visual completa
                  fica dentro da aba Detalhes (com espaço para texto extenso). */}
              <DialogDescription className="sr-only">
                {selectedEntry.description || t('knowledge.storage.details.title')}
              </DialogDescription>
            </DialogHeader>
            <div className="flex-1 min-h-0 overflow-hidden px-6 pt-5">
              <EntryDetails key={selectedEntry.id} entry={selectedEntry} />
            </div>
            <DialogFooter className="px-6 py-3 border-t bg-muted/20 sm:justify-between gap-2">
              <TooltipProvider delayDuration={150}>
                <div className="flex items-center gap-1">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                        onClick={() => {
                          // Fecha o modal de detalhes e abre o confirm de
                          // exclusão. Não empilhamos os dois ao mesmo tempo
                          // para evitar dois Dialogs sobrepostos disputando
                          // o focus trap.
                          const id = selectedEntry.id;
                          setSelectedEntry(null);
                          handleDeleteClick(id);
                        }}
                      >
                        <Trash className="h-4 w-4" />
                        <span className="sr-only">{t('common.actions.delete')}</span>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="top">{t('common.actions.delete')}</TooltipContent>
                  </Tooltip>

                  {selectedEntry.file_path && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-muted-foreground hover:bg-foreground/5 hover:text-foreground"
                          onClick={() => handleDownloadClick(selectedEntry.id)}
                        >
                          <Download className="h-4 w-4" />
                          <span className="sr-only">{t('common.actions.download')}</span>
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="top">{t('common.actions.download')}</TooltipContent>
                    </Tooltip>
                  )}
                </div>
              </TooltipProvider>
              <Button
                variant="default"
                size="sm"
                onClick={() => setSelectedEntry(null)}
              >
                {t('common.actions.close')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        );
      })()}

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('common.messages.deleteConfirmationTitle')}</DialogTitle>
            <DialogDescription>
              {t('common.messages.deleteConfirmationDescription')}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end space-x-2">
            <Button
              variant="outline"
              onClick={() => setIsDeleteDialogOpen(false)}
              disabled={isDeleting}
            >
              {t('common.actions.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('common.actions.delete')}
                </>
              ) : (
                t('common.actions.delete')
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * Renderiza a nota de qualidade (escala 0-10) como 5 estrelas em amber.
 * Cada estrela vale 2 pontos; uma meia estrela cobre o resto ímpar. O score
 * numérico aparece em tooltip (visualmente discreto, mas acessível pra quem
 * precisar do valor exato).
 */
function QualityStars({ score }: { score: number }) {
  const clamped = Math.max(0, Math.min(10, score));
  const filled = Math.floor(clamped / 2);
  const hasHalf = clamped - filled * 2 >= 1;
  const empty = 5 - filled - (hasHalf ? 1 : 0);

  return (
    <div
      className="inline-flex items-center gap-0.5"
      title={`${clamped.toFixed(1)} / 10`}
      aria-label={`Quality ${clamped.toFixed(1)} of 10`}
    >
      {Array.from({ length: filled }).map((_, i) => (
        <Star
          key={`f-${i}`}
          className="h-3.5 w-3.5 text-amber-500 fill-amber-500"
          strokeWidth={1.5}
        />
      ))}
      {hasHalf && (
        <StarHalf
          className="h-3.5 w-3.5 text-amber-500 fill-amber-500"
          strokeWidth={1.5}
        />
      )}
      {Array.from({ length: empty }).map((_, i) => (
        <Star
          key={`e-${i}`}
          className="h-3.5 w-3.5 text-muted-foreground/30"
          strokeWidth={1.5}
        />
      ))}
    </div>
  );
}

function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Header de tabela com drag handle no canto direito pra resize de coluna.
 * Compatível com `<colgroup>` + `table-layout: fixed` — o handle só dispara
 * o callback `onResize`, quem persiste a largura é o componente pai.
 */
function ResizableTableHead({
  children,
  onResize,
  sortKey,
  activeSortKey,
  sortOrder,
  onSort,
}: {
  children: React.ReactNode;
  onResize: (e: React.MouseEvent) => void;
  sortKey?: string;
  activeSortKey?: string;
  sortOrder?: 'asc' | 'desc';
  onSort?: (key: string) => void;
}) {
  // Sortável quando recebe sortKey + onSort. O label vira um botão; o handle
  // de resize continua sendo um <span> separado na borda direita, então
  // arrastar para redimensionar não dispara a ordenação por clique.
  const sortable = !!sortKey && !!onSort;
  const isActive = sortable && activeSortKey === sortKey;

  return (
    <TableHead className="relative select-none">
      {sortable ? (
        <button
          type="button"
          onClick={() => onSort?.(sortKey as string)}
          className="flex items-center gap-1 hover:text-foreground transition-colors"
        >
          <span>{children}</span>
          {isActive ? (
            sortOrder === 'asc'
              ? <ArrowUp className="h-3.5 w-3.5" />
              : <ArrowDown className="h-3.5 w-3.5" />
          ) : (
            <ArrowUpDown className="h-3.5 w-3.5 opacity-40" />
          )}
        </button>
      ) : (
        children
      )}
      <span
        onMouseDown={onResize}
        className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-primary/40 transition-colors"
        aria-hidden
      />
    </TableHead>
  );
}

function QuickFilterCard({
  active,
  onClick,
  label,
  count,
  icon,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  count: number;
  icon?: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm transition-colors ${
        active
          ? 'border-primary/40 bg-primary/5 text-foreground'
          : 'border-border hover:bg-accent text-muted-foreground hover:text-foreground'
      }`}
    >
      {icon}
      <span>{label}</span>
      <Badge variant="secondary" className="h-5 px-1.5 text-[10px] font-medium">
        {count}
      </Badge>
    </button>
  );
}

export { KnowledgeStorageComponent as KnowledgeStorage };
